import { useMemo, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { FormNavigation } from '../components/form/FormNavigation';
import { TextField } from '../components/fields/TextField';
import { SelectField } from '../components/fields/SelectField';
import { RadioGroup } from '../components/fields/RadioGroup';
import { CurrencyInput } from '../components/fields/CurrencyInput';
import { useFormStore, useNavigation } from '../store/formStore';
import { z } from 'zod';
import { BANK_OPTIONS, HOUSING_LOAN_BANK_OPTIONS } from '../config/bankCodes';

// Fonction pour créer le schéma avec validation dynamique
const createPersonalSituationSchema = (borrowerBirthDate?: string) => z.object({
  // Situation familiale
  familyStatus: z.string().min(1, 'Situation familiale requise'),
  familyStatusStartDate: z.string().optional(),
  dependentChildren: z.number().min(0, 'Le nombre d\'enfants doit être positif').max(10, 'Maximum 10 enfants'),

  // Situation de logement
  housingStatus: z.string().min(1, 'Situation de logement requise'),
  monthlyRent: z.number().min(0).optional(),
  monthlyLoanPayment: z.number().min(0).optional(),
  housingLoanStartDate: z.string().optional(),
  housingLoanEndDate: z.string().optional(),
  housingLoanOrganization: z.string().optional(),
  housingStartDate: z.string().min(1, 'Date d\'entrée dans le logement requise'),

  // Informations bancaires
  bankCode: z.string().min(1, 'Banque requise'),
  accountCreationDate: z.string().min(1, 'Date de création du compte requise'),
  accountOwnerCode: z.string().min(1, 'Titulaire du compte requis'),

  // Crédits en cours
  hasOtherCredits: z.string().optional(),
  otherCreditsMthAmount: z.number().min(0).optional(), // uniquement si >= 10 crédits

  // Allocations et pensions
  alimonyReceivedMthAmount: z.number().min(0).optional(),
  housingAllowanceMthAmount: z.number().min(0).optional(),
  alimonyPaidMthAmount: z.number().min(0).optional(),
}).refine((data) => {
  // Validation: la date de situation familiale ne peut pas être dans le futur
  if (data.familyStatusStartDate && data.familyStatusStartDate.length > 0) {
    const selectedDate = new Date(data.familyStatusStartDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to compare only dates
    return selectedDate <= today;
  }
  return true;
}, {
  message: 'La date ne peut pas être dans le futur',
  path: ['familyStatusStartDate']
}).refine((data) => {
  // Validation: la date de situation familiale doit être >= date de naissance
  if (data.familyStatusStartDate && data.familyStatusStartDate.length > 0 && borrowerBirthDate) {
    const statusDate = new Date(data.familyStatusStartDate);
    const birthDate = new Date(borrowerBirthDate);
    birthDate.setHours(0, 0, 0, 0);
    statusDate.setHours(0, 0, 0, 0);
    return statusDate >= birthDate;
  }
  return true;
}, {
  message: 'La date de situation familiale doit être postérieure à votre date de naissance',
  path: ['familyStatusStartDate']
}).refine((data) => {
  // Validation: la date de début du crédit immobilier ne peut pas être dans le futur
  if (data.housingLoanStartDate && data.housingLoanStartDate.length > 0) {
    const selectedDate = new Date(data.housingLoanStartDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate <= today;
  }
  return true;
}, {
  message: 'La date ne peut pas être dans le futur',
  path: ['housingLoanStartDate']
}).refine((data) => {
  // Validation: la date d'entrée dans le logement ne peut pas être dans le futur
  if (data.housingStartDate && data.housingStartDate.length > 0) {
    const selectedDate = new Date(data.housingStartDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate <= today;
  }
  return true;
}, {
  message: 'La date ne peut pas être dans le futur',
  path: ['housingStartDate']
}).refine((data) => {
  // Validation: la date d'entrée dans le logement doit être >= date de naissance
  if (data.housingStartDate && data.housingStartDate.length > 0 && borrowerBirthDate) {
    const housingDate = new Date(data.housingStartDate);
    const birthDate = new Date(borrowerBirthDate);
    birthDate.setHours(0, 0, 0, 0);
    housingDate.setHours(0, 0, 0, 0);
    return housingDate >= birthDate;
  }
  return true;
}, {
  message: 'La date d\'entrée dans le logement doit être postérieure à votre date de naissance',
  path: ['housingStartDate']
}).refine((data) => {
  // Validation: la date de début du crédit immobilier doit être >= date de naissance
  if (data.housingLoanStartDate && data.housingLoanStartDate.length > 0 && borrowerBirthDate) {
    const loanDate = new Date(data.housingLoanStartDate);
    const birthDate = new Date(borrowerBirthDate);
    birthDate.setHours(0, 0, 0, 0);
    loanDate.setHours(0, 0, 0, 0);
    return loanDate >= birthDate;
  }
  return true;
}, {
  message: 'La date de début du crédit doit être postérieure à votre date de naissance',
  path: ['housingLoanStartDate']
}).refine((data) => {
  // Validation: la date de création du compte ne peut pas être dans le futur
  if (data.accountCreationDate && data.accountCreationDate.length > 0) {
    const selectedDate = new Date(data.accountCreationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate <= today;
  }
  return true;
}, {
  message: 'La date ne peut pas être dans le futur',
  path: ['accountCreationDate']
}).refine((data) => {
  // Validation: la date de création du compte doit être >= date de naissance + 14 ans (spec p.19)
  if (data.accountCreationDate && data.accountCreationDate.length > 0 && borrowerBirthDate) {
    const accountDate = new Date(data.accountCreationDate);
    const birthDate = new Date(borrowerBirthDate);
    const minDate = new Date(birthDate);
    minDate.setFullYear(birthDate.getFullYear() + 14);
    accountDate.setHours(0, 0, 0, 0);
    minDate.setHours(0, 0, 0, 0);
    return accountDate >= minDate;
  }
  return true;
}, {
  message: 'La date de création du compte doit être au moins 14 ans après votre date de naissance',
  path: ['accountCreationDate']
}).refine((data) => {
  // Validation conditionnelle pour la date de situation familiale (spec p.42 : obligatoire si ≠ 'C')
  if (['M', 'P', 'D', 'S', 'V', 'K'].includes(data.familyStatus)) {
    return data.familyStatusStartDate && data.familyStatusStartDate.length > 0;
  }
  return true;
}, {
  message: 'Date de début de situation requise pour ce statut',
  path: ['familyStatusStartDate']
}).refine((data) => {
  if (data.housingStatus === 'L') {
    return !!(data.monthlyRent && data.monthlyRent > 0);
  }
  return true;
}, {
  message: 'Le loyer mensuel est requis',
  path: ['monthlyRent']
}).refine((data) => {
  if (data.housingStatus === 'A') {
    return !!(data.monthlyLoanPayment && data.monthlyLoanPayment > 0);
  }
  return true;
}, {
  message: 'La mensualité est requise',
  path: ['monthlyLoanPayment']
}).refine((data) => {
  if (data.housingStatus === 'A') {
    return !!(data.housingLoanStartDate && data.housingLoanStartDate.length > 0 &&
           data.housingLoanEndDate && data.housingLoanEndDate.length > 0 &&
           data.housingLoanOrganization && data.housingLoanOrganization.length > 0);
  }
  return true;
}, {
  message: 'Informations du crédit immobilier incomplètes',
  path: ['housingLoanOrganization']
});

interface PersonalSituationFormData {
  familyStatus: string;
  familyStatusStartDate?: string;
  dependentChildren: number;
  housingStatus: string;
  monthlyRent?: number;
  monthlyLoanPayment?: number;
  housingLoanStartDate?: string;
  housingLoanEndDate?: string;
  housingLoanOrganization?: string;
  housingStartDate: string;
  bankCode: string;
  accountCreationDate: string;
  accountOwnerCode: string;
  hasOtherCredits?: string; // 'yes' ou 'no'
  otherCreditsMthAmount?: number; // uniquement si >= 10 crédits
  alimonyReceivedMthAmount?: number;
  housingAllowanceMthAmount?: number;
  alimonyPaidMthAmount?: number;
}

export function PersonalSituationStep() {
  const { formData, updateField, errors } = useFormStore();
  const { goToPreviousStep, goToNextStep, canGoBack } = useNavigation();

  // Helpers pour les dates en heure locale (évite le décalage UTC de toISOString)
  const toLocalDateStr = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  // Date du jour pour bloquer les dates futures
  const today = toLocalDateStr(new Date());
  // Lendemain pour les dates de fin de crédit (Cofidis exige une date strictement future)
  const tomorrow = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return toLocalDateStr(d);
  })();

  // Date de naissance de l'emprunteur pour validation
  const borrowerBirthDate = formData.birthDate || '';

  // Date minimum pour la création du compte bancaire (naissance + 14 ans — spec p.19)
  const minAccountCreationDate = useMemo(() => {
    if (!borrowerBirthDate) return '';
    const birthDate = new Date(borrowerBirthDate);
    birthDate.setFullYear(birthDate.getFullYear() + 14);
    return birthDate.toISOString().split('T')[0];
  }, [borrowerBirthDate]);

  // Créer le schéma avec la date de naissance
  const personalSituationSchema = useMemo(
    () => createPersonalSituationSchema(borrowerBirthDate),
    [borrowerBirthDate]
  );

  const [accountDateTouched, setAccountDateTouched] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors: formErrors, isValid, touchedFields }
  } = useForm<PersonalSituationFormData>({
    resolver: zodResolver(personalSituationSchema),
    defaultValues: {
      familyStatus: formData.familyStatus || '',
      familyStatusStartDate: formData.familyStatusStartDate || '',
      dependentChildren: formData.dependentChildren !== undefined ? formData.dependentChildren : 0,
      housingStatus: formData.housingStatus || '',
      monthlyRent: formData.monthlyRent || undefined,
      monthlyLoanPayment: formData.monthlyLoanPayment || undefined,
      housingLoanStartDate: formData.housingLoanStartDate || '',
      housingLoanEndDate: formData.housingLoanEndDate || '',
      housingLoanOrganization: formData.housingLoanOrganization || '',
      housingStartDate: formData.housingStartDate || '',
      bankCode: formData.bankCode || '',
      accountCreationDate: formData.accountCreationDate || '',
      accountOwnerCode: formData.hasCoBorrower ? (formData.accountOwnerCode || '') : '01',
      hasOtherCredits: formData.hasOtherCredits || 'no',
      alimonyReceivedMthAmount: formData.alimonyReceivedMthAmount || undefined,
      housingAllowanceMthAmount: formData.housingAllowanceMthAmount || undefined,
      alimonyPaidMthAmount: formData.alimonyPaidMthAmount || undefined
    },
    mode: 'onChange'
  });

  useEffect(() => { trigger(); }, []);

  const fieldError = (name: keyof PersonalSituationFormData): string | undefined =>
    touchedFields[name] ? formErrors[name]?.message : undefined;

  const touchField = (name: keyof PersonalSituationFormData) => () =>
    setValue(name, watch(name) as any, { shouldTouch: true, shouldValidate: true });

  const watchedHousingStatus = watch('housingStatus');
  const watchedFamilyStatus = watch('familyStatus');
  const watchedHasOtherCredits = watch('hasOtherCredits');
  const watchedDependentChildren = watch('dependentChildren');

  // --- Crédits externes (multi-crédits) ---
  interface ExternalCredit {
    type: string;
    organization: string;
    startDate: string;
    endDate: string;
    monthlyAmount: number;
  }

  const [credits, setCredits] = useState<ExternalCredit[]>(
    (formData.externalCredits as ExternalCredit[]) || []
  );
  const [otherCreditsMthOverflow, setOtherCreditsMthOverflow] = useState<number>(
    formData.otherCreditsMthAmount || 0
  );
  const [creditErrors, setCreditErrors] = useState<Record<number, Record<string, string>>>({});

  const addCredit = () => {
    if (credits.length >= 10) return;
    const newCredits = [...credits, { type: '', organization: '', startDate: '', endDate: '', monthlyAmount: 0 }];
    setCredits(newCredits);
    updateField('externalCredits', newCredits);
  };

  const removeCredit = (index: number) => {
    const newCredits = credits.filter((_, i) => i !== index);
    setCredits(newCredits);
    updateField('externalCredits', newCredits);
    // Renuméroter les erreurs
    setCreditErrors(prev => {
      const updated: Record<number, Record<string, string>> = {};
      Object.entries(prev).forEach(([k, v]) => {
        const i = Number(k);
        if (i < index) updated[i] = v;
        else if (i > index) updated[i - 1] = v;
      });
      return updated;
    });
  };

  const updateCredit = (index: number, field: keyof ExternalCredit, value: any) => {
    const newCredits = credits.map((c, i) => i === index ? { ...c, [field]: value } : c);
    setCredits(newCredits);
    updateField('externalCredits', newCredits);
  };

  const creditsValid = watchedHasOtherCredits !== 'yes' || (
    credits.length > 0 &&
    credits.every((c, i) =>
      c.type && c.organization && c.startDate && c.monthlyAmount > 0 &&
      (c.type === 'PREREN' || (c.endDate && c.endDate > today)) &&
      !creditErrors[i]?.startDate &&
      !creditErrors[i]?.endDate
    )
  );

  const allFamilyStatusOptions = [
    { value: 'C', label: 'Célibataire' },
    { value: 'M', label: 'Marié(e)' },
    { value: 'P', label: 'Pacsé(e)' },
    { value: 'K', label: 'En union libre' },
    { value: 'D', label: 'Divorcé(e)' },
    { value: 'V', label: 'Veuf/Veuve' },
    { value: 'S', label: 'Séparé(e)' }
  ];

  // Si un co-emprunteur existe, retirer l'option "Célibataire"
  const familyStatusOptions = formData.hasCoBorrower
    ? allFamilyStatusOptions.filter(option => option.value !== 'C')
    : allFamilyStatusOptions;
  
  const housingStatusOptions = [
    { value: 'L', label: 'Locataire' },
    { value: 'A', label: 'Accédant (avec prêt immobilier)' },
    { value: 'P', label: 'Propriétaire sans prêt' },
    { value: 'V', label: 'Hébergé par la famille' },
    { value: 'E', label: 'Logé par l\'employeur' },
    { value: 'X', label: 'Autre' }
  ];
  
  const onSubmit = (data: PersonalSituationFormData) => {
    // Mettre à jour toutes les données dans le store
    Object.entries(data).forEach(([key, value]) => {
      updateField(key, value);
    });
    
    goToNextStep();
  };
  
  const handleFieldChange = (fieldName: keyof PersonalSituationFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      let value: any = e.target.value;

      // Convertir en nombre pour les champs numériques
      if (['dependentChildren', 'monthlyRent', 'monthlyLoanPayment'].includes(fieldName)) {
        value = value ? parseFloat(value) : (fieldName === 'dependentChildren' ? 0 : undefined);
      }

      setValue(fieldName, value, { shouldValidate: true });
      updateField(fieldName, value);
    };

  // Libellés pour les dates de situation familiale
  const getFamilyStatusDateLabel = (status: string): string => {
    const labels: Record<string, string> = {
      'M': 'Date de mariage',
      'P': 'Date de PACS',
      'D': 'Date de divorce',
      'S': 'Date de séparation',
      'V': 'Date de veuvage',
      'K': 'Date de début de vie commune'
    };
    return labels[status] || 'Date de début de situation';
  };
    
  const handleStatusChange = (fieldName: keyof PersonalSituationFormData) => (value: string) => {
    setValue(fieldName, value, { shouldValidate: true, shouldTouch: true });
    updateField(fieldName, value);

    // Si logement P ou V, vider housingAllowanceMthAmount (spec p.24 : interdit pour ces statuts)
    if (fieldName === 'housingStatus' && ['P', 'V'].includes(value)) {
      setValue('housingAllowanceMthAmount', undefined, { shouldValidate: true });
      updateField('housingAllowanceMthAmount', 0);
    }

    // Si célibataire ou divorcé, nettoyer les données du co-emprunteur
    if (fieldName === 'familyStatus' && ['C', 'D'].includes(value)) {
      updateField('hasCoBorrower', false);
      // Nettoyer les données du co-emprunteur pour éviter les erreurs API
      [
        'coBorrowerTitle', 'coBorrowerFirstName', 'coBorrowerLastName',
        'coBorrowerBirthDate', 'coBorrowerBirthCity', 'coBorrowerBirthDepartment',
        'coBorrowerBirthCountry', 'coBorrowerNationality', 'coBorrowerEmail',
        'coBorrowerPhone', 'coBorrowerEmploymentStatus', 'coBorrowerMonthlySalary',
        'coBorrowerSeniorityMonths', 'coBorrowerCompanyName', 'coBorrowerContractEndDate',
        'coBorrowerRetirementPension', 'coBorrowerDisabilityPension',
        'coBorrowerReversionaryPension', 'coBorrowerBirthName'
      ].forEach(field => {
        updateField(field as any, undefined);
      });
    }
  };
  
  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Votre situation personnelle</CardTitle>
          <CardDescription>
            Informations sur votre situation familiale, logement et banque
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            
            {/* Situation familiale */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-900">Situation familiale</h3>
              
              <div>
                <RadioGroup
                  label="Situation familiale"
                  options={familyStatusOptions}
                  value={watchedFamilyStatus}
                  onChange={handleStatusChange('familyStatus')}
                  name="familyStatus"
                  required
                  error={fieldError('familyStatus') || errors.familyStatus}
                />
              </div>

              {/* Incompatibilité co-emprunteur / statut séparé */}
              {formData.hasCoBorrower && ['S', 'D'].includes(watchedFamilyStatus) && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                  Un co-emprunteur n'est pas compatible avec le statut "{watchedFamilyStatus === 'S' ? 'Séparé(e)' : 'Divorcé(e)'}". Veuillez retirer le co-emprunteur ou choisir un autre statut familial.
                </div>
              )}

              {/* Date de situation familiale (sauf pour célibataire) — spec p.42 */}
              {['M', 'P', 'D', 'S', 'V', 'K'].includes(watchedFamilyStatus) && (
                <div>
                  <TextField
                    {...register('familyStatusStartDate')}
                    type="date"
                    label={getFamilyStatusDateLabel(watchedFamilyStatus)}
                    required
                    min={borrowerBirthDate}
                    max={today}
                    error={fieldError('familyStatusStartDate') || errors.familyStatusStartDate}
                    onChange={handleFieldChange('familyStatusStartDate')}
                    helperText="Date entre votre naissance et aujourd'hui"
                  />
                </div>
              )}

              <div>
                <TextField
                  {...register('dependentChildren', { valueAsNumber: true })}
                  type="number"
                  label="Nombre d'enfants à charge"
                  min="0"
                  max="10"
                  required
                  error={fieldError('dependentChildren') || errors.dependentChildren}
                  onChange={(e) => {
                    handleFieldChange('dependentChildren')(e);
                    // Si 0 enfant, vider les champs réservés aux familles avec enfants
                    if (!e.target.value || Number(e.target.value) === 0) {
                      updateField('otherIncome', 0);
                      setValue('alimonyReceivedMthAmount', undefined, { shouldValidate: true });
                      updateField('alimonyReceivedMthAmount', 0);
                    }
                  }}
                  helperText="Enfants de moins de 18 ans ou étudiants de moins de 25 ans"
                />
              </div>
            </div>
            
            {/* Situation de logement */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-900">Situation de logement</h3>
              
              <div>
                <RadioGroup
                  label="Situation de logement"
                  options={housingStatusOptions}
                  value={watchedHousingStatus}
                  onChange={handleStatusChange('housingStatus')}
                  name="housingStatus"
                  required
                  error={fieldError('housingStatus') || errors.housingStatus}
                />
              </div>
              
              {watchedHousingStatus === 'L' && (
                <div>
                  <CurrencyInput
                    value={formData.monthlyRent || 0}
                    onChange={(value) => {
                      setValue('monthlyRent', value, { shouldValidate: true });
                      updateField('monthlyRent', value);
                    }}
                    onBlur={touchField('monthlyRent')}
                    label="Montant du loyer mensuel"
                    placeholder="1 000 €"
                    required
                    error={fieldError('monthlyRent') || errors.monthlyRent}
                    helperText="Charges comprises"
                    showProgress={false}
                  />
                </div>
              )}
              
              {watchedHousingStatus === 'A' && (
                <div className="space-y-4">
                  <CurrencyInput
                    value={formData.monthlyLoanPayment || 0}
                    onChange={(value) => {
                      setValue('monthlyLoanPayment', value, { shouldValidate: true });
                      updateField('monthlyLoanPayment', value);
                    }}
                    onBlur={touchField('monthlyLoanPayment')}
                    label="Mensualité prêt immobilier"
                    placeholder="900 €"
                    showProgress={false}
                    required
                    error={fieldError('monthlyLoanPayment') || errors.monthlyLoanPayment}
                    helperText="Assurance comprise"
                  />
                  <TextField
                    {...register('housingLoanStartDate')}
                    type="date"
                    label="Date de début du crédit immobilier"
                    required={watchedHousingStatus === 'A'}
                    min={borrowerBirthDate}
                    max={today}
                    error={fieldError('housingLoanStartDate')}
                    onChange={handleFieldChange('housingLoanStartDate')}
                  />
                  <TextField
                    {...register('housingLoanEndDate')}
                    type="date"
                    label="Date de fin du crédit immobilier"
                    required={watchedHousingStatus === 'A'}
                    error={fieldError('housingLoanEndDate')}
                    onChange={handleFieldChange('housingLoanEndDate')}
                  />
                  <SelectField
                    {...register('housingLoanOrganization')}
                    label="Organisme prêteur"
                    options={HOUSING_LOAN_BANK_OPTIONS}
                    required={watchedHousingStatus === 'A'}
                    error={fieldError('housingLoanOrganization')}
                    onChange={handleFieldChange('housingLoanOrganization')}
                  />
                </div>
              )}
              
              <div>
                <TextField
                  {...register('housingStartDate')}
                  type="date"
                  label="Date d'entrée dans le logement"
                  required
                  min={borrowerBirthDate}
                  max={today}
                  error={fieldError('housingStartDate') || errors.housingStartDate}
                  onChange={handleFieldChange('housingStartDate')}
                />
              </div>
            </div>
            
            {/* Informations bancaires */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-900">Informations bancaires</h3>
              
              <div>
                <SelectField
                  {...register('bankCode')}
                  label="Votre banque principale"
                  options={BANK_OPTIONS}
                  required
                  error={fieldError('bankCode') || errors.bankCode}
                  onChange={handleFieldChange('bankCode')}
                />
              </div>
              
              <div>
                <TextField
                  type="date"
                  label="Date de création du compte"
                  required
                  min={minAccountCreationDate}
                  max={today}
                  defaultValue={formData.accountCreationDate || ''}
                  error={accountDateTouched ? (formErrors.accountCreationDate?.message || errors.accountCreationDate) : undefined}
                  onChange={(e) => {
                    const value = e.target.value;
                    setValue('accountCreationDate', value, { shouldValidate: true });
                    updateField('accountCreationDate', value);
                  }}
                  onBlur={() => setAccountDateTouched(true)}
                />
              </div>

              {formData.hasCoBorrower ? (
                <div>
                  <SelectField
                    {...register('accountOwnerCode')}
                    label="Titulaire du compte"
                    options={[
                      { value: '01', label: 'Emprunteur seul' },
                      { value: '02', label: 'Co-emprunteur seul' },
                      { value: '03', label: 'Compte joint' }
                    ]}
                    required
                    error={fieldError('accountOwnerCode') || errors.accountOwnerCode}
                    onChange={handleFieldChange('accountOwnerCode')}
                    helperText="Indiquez qui est titulaire du compte bancaire"
                  />
                </div>
              ) : (
                <div className="field-group">
                  <label className="form-label">Titulaire du compte</label>
                  <div className="input-field bg-neutral-50 cursor-not-allowed text-neutral-600 flex items-center">
                    Emprunteur seul
                  </div>
                </div>
              )}
            </div>

            {/* Crédits en cours */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-900">Crédits en cours</h3>

              <RadioGroup
                label="Avez-vous d'autres crédits en cours ?"
                options={[
                  { value: 'no', label: 'Non' },
                  { value: 'yes', label: 'Oui' }
                ]}
                value={watchedHasOtherCredits || 'no'}
                onChange={(value) => {
                  handleStatusChange('hasOtherCredits')(value);
                  if (value === 'no') {
                    setCredits([]);
                    updateField('externalCredits', []);
                  }
                }}
                name="hasOtherCredits"
                error={formErrors.hasOtherCredits?.message || errors.hasOtherCredits}
              />

              {watchedHasOtherCredits === 'yes' && (
                <div className="space-y-3">
                  {credits.map((credit, index) => (
                    <div key={index} className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-neutral-900">Crédit {index + 1}</h4>
                        <button
                          type="button"
                          onClick={() => removeCredit(index)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                          title="Supprimer ce crédit"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6" />
                            <path d="M14 11v6" />
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                          </svg>
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="field-group">
                          <label className="form-label">Type de crédit <span className="text-red-500">*</span></label>
                          <select
                            className="input-field"
                            value={credit.type}
                            onChange={(e) => updateCredit(index, 'type', e.target.value)}
                          >
                            <option value="">Sélectionner...</option>
                            <option value="PREAUT">Crédit automobile</option>
                            <option value="PRECON">Crédit consommation / amortissable</option>
                            <option value="PREREN">Crédit renouvelable</option>
                          </select>
                        </div>

                        <div className="field-group">
                          <label className="form-label">Organisme prêteur <span className="text-red-500">*</span></label>
                          <select
                            className="input-field"
                            value={credit.organization}
                            onChange={(e) => updateCredit(index, 'organization', e.target.value)}
                          >
                            <option value="">Sélectionner...</option>
                            {HOUSING_LOAN_BANK_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>

                        <div className="field-group">
                          <label className="form-label">Date de début <span className="text-red-500">*</span></label>
                          <input
                            type="date"
                            className={`input-field${creditErrors[index]?.startDate ? ' border-red-500 focus:ring-red-500' : ''}`}
                            value={credit.startDate}
                            min={borrowerBirthDate}
                            max={today}
                            onChange={(e) => {
                              const val = e.target.value;
                              let error = '';
                              if (val && borrowerBirthDate && val < borrowerBirthDate) {
                                error = 'La date doit être postérieure à votre date de naissance';
                              } else if (val && val > today) {
                                error = 'La date de début ne peut pas être dans le futur';
                              }
                              setCreditErrors(prev => {
                                const updated = { ...prev, [index]: { ...(prev[index] || {}) } };
                                if (error) updated[index].startDate = error;
                                else delete updated[index].startDate;
                                return updated;
                              });
                              updateCredit(index, 'startDate', val);
                            }}
                          />
                          {creditErrors[index]?.startDate && (
                            <p className="mt-1 text-xs text-red-600">{creditErrors[index].startDate}</p>
                          )}
                        </div>

                        {credit.type !== 'PREREN' && (
                          <div className="field-group">
                            <label className="form-label">Date de fin prévue <span className="text-red-500">*</span></label>
                            <input
                              type="date"
                              className={`input-field${creditErrors[index]?.endDate ? ' border-red-500 focus:ring-red-500' : ''}`}
                              value={credit.endDate}
                              min={tomorrow}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val && val <= today) {
                                  setCreditErrors(prev => ({ ...prev, [index]: { ...(prev[index] || {}), endDate: 'La date de fin doit être strictement dans le futur' } }));
                                } else {
                                  setCreditErrors(prev => {
                                    const updated = { ...prev };
                                    if (updated[index]) {
                                      const { endDate: _, ...rest } = updated[index];
                                      updated[index] = rest;
                                    }
                                    return updated;
                                  });
                                }
                                updateCredit(index, 'endDate', val);
                              }}
                            />
                            {creditErrors[index]?.endDate && (
                              <p className="mt-1 text-xs text-red-600">{creditErrors[index].endDate}</p>
                            )}
                          </div>
                        )}

                        <div className="field-group">
                          <label className="form-label">Mensualité <span className="text-red-500">*</span></label>
                          <CurrencyInput
                            value={credit.monthlyAmount}
                            onChange={(value) => updateCredit(index, 'monthlyAmount', value)}
                            placeholder="500 €"
                            min={1}
                            step={10}
                            showProgress={false}
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  {credits.length === 0 && (
                    <p className="text-sm text-amber-600">Veuillez ajouter au moins un crédit.</p>
                  )}

                  {credits.length < 10 && (
                    <button
                      type="button"
                      onClick={addCredit}
                      className="w-full border-2 border-dashed border-neutral-300 rounded-lg p-3 text-sm text-neutral-600 hover:border-primary-400 hover:text-primary-600 transition-colors"
                    >
                      + Ajouter un crédit
                    </button>
                  )}

                  {credits.length >= 10 && (
                    <CurrencyInput
                      value={otherCreditsMthOverflow}
                      onChange={(value) => {
                        setOtherCreditsMthOverflow(value);
                        updateField('otherCreditsMthAmount', value);
                      }}
                      label="Mensualité totale des crédits supplémentaires"
                      helperText="Pour les crédits au-delà des 10 déjà renseignés"
                      min={0}
                      step={10}
                      showProgress={false}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Allocations et pensions */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-900">Allocations et pensions</h3>
              <p className="text-sm text-neutral-600">
                Ces informations sont optionnelles mais peuvent améliorer votre dossier
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <CurrencyInput
                    value={formData.otherIncome ?? 0}
                    onChange={(value) => updateField('otherIncome', value)}
                    label="Allocations familiales"
                    placeholder="200 €"
                    min={0}
                    step={10}
                    showProgress={false}
                    disabled={!formData.dependentChildren || formData.dependentChildren === 0}
                    helperText={!formData.dependentChildren || formData.dependentChildren === 0 ? "Renseignez d'abord le nombre d'enfants" : undefined}
                  />
                </div>

                {watchedDependentChildren > 0 && (
                  <div>
                    <CurrencyInput
                      value={formData.alimonyReceivedMthAmount ?? 0}
                      onChange={(value) => {
                        setValue('alimonyReceivedMthAmount', value, { shouldValidate: true });
                        updateField('alimonyReceivedMthAmount', value);
                      }}
                      label="Pension alimentaire reçue"
                      placeholder="1 000 €"
                      error={formErrors.alimonyReceivedMthAmount?.message || errors.alimonyReceivedMthAmount}
                      helperText="Montant mensuel perçu"
                      min={0}
                      step={10}
                      showProgress={false}
                    />
                  </div>
                )}

                {!['P', 'V'].includes(watchedHousingStatus) && (
                  <div>
                    <CurrencyInput
                      value={formData.housingAllowanceMthAmount ?? 0}
                      onChange={(value) => {
                        setValue('housingAllowanceMthAmount', value, { shouldValidate: true });
                        updateField('housingAllowanceMthAmount', value);
                      }}
                      label="Allocation logement (CAF)"
                      placeholder="1 000 €"
                      error={formErrors.housingAllowanceMthAmount?.message || errors.housingAllowanceMthAmount}
                      helperText="APL, ALF ou ALS"
                      min={0}
                      step={10}
                      showProgress={false}
                    />
                  </div>
                )}

                {watchedDependentChildren > 0 && (
                  <div>
                    <CurrencyInput
                      value={formData.alimonyPaidMthAmount ?? 0}
                      onChange={(value) => {
                        setValue('alimonyPaidMthAmount', value, { shouldValidate: true });
                        updateField('alimonyPaidMthAmount', value);
                      }}
                      label="Pension alimentaire versée"
                      placeholder="1 000 €"
                      error={formErrors.alimonyPaidMthAmount?.message || errors.alimonyPaidMthAmount}
                      helperText="Montant mensuel versé"
                      min={0}
                      step={10}
                      showProgress={false}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Note informative */}
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <h3 className="font-semibold text-primary-800 mb-2">
                ℹ️ Pourquoi ces informations ?
              </h3>
              <ul className="text-sm text-primary-700 space-y-1">
                <li>• Votre situation familiale influence l'analyse de votre dossier</li>
                <li>• Les informations de logement permettent d'évaluer vos charges fixes</li>
                <li>• Vos crédits en cours sont pris en compte dans le calcul de votre endettement</li>
                <li>• Les allocations perçues augmentent votre capacité de remboursement</li>
                <li>• Les pensions versées sont considérées comme des charges</li>
                <li>• La banque principale sert au virement des fonds</li>
              </ul>
            </div>
            
            <FormNavigation
              onPrevious={goToPreviousStep}
              onNext={handleSubmit(onSubmit)}
              canGoBack={canGoBack}
              canGoNext={isValid && creditsValid && !(formData.hasCoBorrower && ['S', 'D'].includes(watchedFamilyStatus))}
              nextButtonText="Continuer"
            />
          </form>
        </CardContent>
      </Card>
    </div>
  );
}